import {ArrowRightOutlined, ArrowUpOutlined, BarChartOutlined, LineChartOutlined} from "@ant-design/icons";
import {DndContext, DragEndEvent, PointerSensor, useDroppable, useSensor, useSensors} from '@dnd-kit/core';
import {Col, Radio, Row, Spin} from "antd";
import {RadioChangeEvent} from "antd/es";
import React, {ComponentProps, MutableRefObject, useEffect, useRef, useState} from "react";
import {ChartOptions, ChartResults} from "../../../actions/queryBuilderApi";
import {Query, QueryField} from "../types/Query";
import {Chart} from "./Chart";
import {ChartTypeSelector} from "./ChartTypeSelector";
import {QueryVisualizerFilterDetails} from "./QueryVisualizerFilterDetails";
import {SelectField} from "./SelectField";
import {ValueFields} from "./ValueFields";
import {ChartType} from "./types/ChartType";
import {DragCurrent} from "./types/DragCurrent";
import {ReactComponent as SortAscendIcon} from "./icons/sort-ascend.svg"
import {ReactComponent as SortDescendIcon} from "./icons/sort-descend.svg"
import {ReactComponent as SortIcon} from "./icons/sort-icon.svg"
import {ReactComponent as QueryBuilderLogo} from '../../../components/query-builder/icons/QB-Icon.svg'
import {QueryMetaData} from "../types/QueryMetaData";
import {QUERY_BUILDER_TAB, QueryBuilderTabs} from "../../../components/query-builder/QueryBuilderTabs";

import qbStyles from '../../../components/query-builder/QueryBuilderTemplate.module.scss'
import styles from './Visualisations.module.scss';

// region utility methods

const removeFieldIfPresent = (field: QueryField, fields: QueryField[], setFields: (fields: QueryField[]) => void) => {
    const retainedFields = fields.filter(checkField => checkField.code !== field.code)
    if (retainedFields.length < fields.length) {
        setFields(retainedFields)
    }
}

const determineFieldsAvailableToDrag = (query: Query | undefined, xFields: QueryField[], yFields: QueryField[]) => {
    if (query === undefined) {
        return []
    }
    const availableFields = [
        ...query.columns.filter(column => !isSelected(column, xFields, yFields)),
        ...query.rows.filter(row => !isSelected(row, xFields, yFields))
    ]
    return availableFields
}

const isSelected = (field: QueryField, xFields: QueryField[], yFields: QueryField[]): boolean => {
    return fieldsContain(field, xFields) || fieldsContain(field, yFields)
}

const fieldsContain = (field: QueryField, fieldsToCheck: QueryField[]): boolean => {
    return fieldsToCheck.find(fieldToCheck => fieldToCheck.code === field.code) !== undefined
}

const pickRelevantAppliedFilters = (appliedFilters: Record<string, string[]>, xFields: QueryField[], yFields: QueryField[]): Record<string, string[]> => {
    return Object.entries(appliedFilters)
        .filter(([code]) => xFields.find(xField => xField.code === code) || yFields.find(yField => yField.code === code))
        .reduce((prev, [code, values]) => {
            return {
                ...prev,
                [code]: values
            }
        }, {})
}

// endregion utility methods

// region supplementary components

type InitialFieldsProps = {
    isLoading: boolean
    children?: React.ReactNode
}

const InitialFields = ({isLoading, children}: InitialFieldsProps) => {
    const {setNodeRef} = useDroppable({id: 'select'})
    return (
        <Row className={styles.selectFields} ref={setNodeRef} data-testid="select-fields-area">
            {isLoading ? <Spin/> : children}
        </Row>
    )
}

type AxisProps = {
    chartType?: ChartType
    children: React.ReactNode
}

type DragAndDropAreaProps = {
    label?: string
}

const DragAndDropArea = ({label}: DragAndDropAreaProps) => {
    return <span className={styles.dragAndDropHint}>{label? label : 'Drop fields here'}</span>
}

const XAxis = ({children}: AxisProps) => {
    const {setNodeRef} = useDroppable({
        id: 'x-axis',
    });

    return (
        <div className={styles['x-axis']} ref={setNodeRef} data-testid='x-fields-area'>
            <span className={styles['axis-arrow']}><ArrowRightOutlined/></span>
            <span className={styles['axis-title']}>X - Axis</span>
            {children}
            <DragAndDropArea/>
        </div>
    )
}

const YAxis = ({chartType, children}: AxisProps) => {
    const {setNodeRef} = useDroppable({
        id: 'y-axis',
    });

    if (ChartType.BAR === chartType) {
        return null
    } else {
        let arrowIcon = <ArrowUpOutlined/>
        let label = 'Y - Axis'
        if (ChartType.LINE === chartType) {
            label = 'Line'
        } else if (ChartType.STACKED_BAR === chartType) {
            label = 'Stack'
        }
        return (
            <div className={styles['y-axis']} ref={setNodeRef} data-testid='y-fields-area'>
                <span className={styles['axis-arrow']}>{arrowIcon}</span>
                <span className={styles['axis-title']}>{label}</span>
                {children}
                <DragAndDropArea/>
            </div>
        )
    }
}

type SelectFieldsProps = {
    queryMetaData?: QueryMetaData
    fields: QueryField[],
    from: DragCurrent["from"],
    appliedFilters: Record<string, string[]>
    setAppliedFilters: (appliedFilters: Record<string, string[]>) => void
}

const SelectFields = ({queryMetaData, fields, from, appliedFilters, setAppliedFilters}: SelectFieldsProps) => {

    if (!queryMetaData) {
        return <Spin/>
    }

    return <div className={styles['field-area']}>
        {fields.map(field => {
            const fieldAppliedFilters = appliedFilters[field.code] ?? []
            return <SelectField
                queryMetaData={queryMetaData}
                key={field.code}
                field={field}
                from={from}
                appliedFilters={fieldAppliedFilters}
                setAppliedFilters={(newAppliedFilters) => {
                    setAppliedFilters({
                        ...appliedFilters,
                        [field.code]: newAppliedFilters
                    })
                }}
            />;
        })}
    </div>
}

// endregion supplementary components

type VisualisationsProps = {
    queryId?: string
    queryMetaData?: QueryMetaData
    defaultQueryVisualisationOptions?: ChartOptions
    defaultQueryVisualisationResults?: ChartResults
    dragEventRef?: MutableRefObject<ComponentProps<typeof DndContext>['onDragEnd']>
    currentTab?: QUERY_BUILDER_TAB
    setQueryBuilderTab: (queryBuilderTab: QUERY_BUILDER_TAB) => void
    isQueryVisualizerTabEnabled: boolean
    isCohortAnalyser: boolean
}

export const Visualisations = ({
                                   queryId,
                                   queryMetaData,
                                   defaultQueryVisualisationOptions,
                                   defaultQueryVisualisationResults,
                                   dragEventRef,
                                   setQueryBuilderTab,
                                   isQueryVisualizerTabEnabled,
                                   isCohortAnalyser
                               }: VisualisationsProps) => {

    const query = queryMetaData?.query

    const isMetadataLoading = queryId !== undefined && (!queryMetaData || queryMetaData.id !== queryId)

    const [isDefaultChartModified, setIsDefaultChartModified] = useState<boolean>(false)

    const [xFields, setXFields] = useState<QueryField[]>([])
    const [yFields, setYFields] = useState<QueryField[]>([])
    const [valueField, setValueField] = useState<QueryField | undefined>(undefined)
    const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({})
    const [chartType, setChartType] = useState<ChartType>(ChartType.BAR)
    const [sort, setSort] = useState<number>(0)
    const [topTen, setTopTen] = useState<boolean>(false)
    const [isSortDisabled, setSortDisabled] = useState<boolean>(false)

    // apply the pre-calculated chart configuration so the chart is drawn when the QV page loads
    useEffect(() => {
        if (defaultQueryVisualisationOptions && !isDefaultChartModified) {
            if (defaultQueryVisualisationOptions.xFields) setXFields(defaultQueryVisualisationOptions.xFields)
            if (defaultQueryVisualisationOptions.yFields) setYFields(defaultQueryVisualisationOptions.yFields)
            if (defaultQueryVisualisationOptions.values?.[0]) setValueField(defaultQueryVisualisationOptions.values[0])
            if (defaultQueryVisualisationOptions.chartType) setChartType(defaultQueryVisualisationOptions.chartType)
            if (defaultQueryVisualisationOptions.filters) setAppliedFilters(defaultQueryVisualisationOptions.filters)
            if (defaultQueryVisualisationOptions.sort !== undefined) setSort(defaultQueryVisualisationOptions.sort)
            if (defaultQueryVisualisationOptions.topTen !== undefined) setTopTen(defaultQueryVisualisationOptions.topTen)
        }
    }, [defaultQueryVisualisationOptions, xFields, yFields, isDefaultChartModified])

    // don't think this will ever be used. this is when there is no default chart options provided (which should never happen)
    useEffect(() => {
        if (query && !defaultQueryVisualisationOptions && !valueField) {
            setValueField(query.values[0])
        }
    }, [query, defaultQueryVisualisationOptions, valueField])

    // reset chart options between chart switching
    const isFirstRender = useRef(true)

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
        } else {
            if (chartType === ChartType.BAR) {
                setYFields([])
            }
            if (chartType !== ChartType.LINE) {
                setSortDisabled(false)
            }
        }
    }, [chartType, xFields.length])

    const relevantFilters = pickRelevantAppliedFilters(appliedFilters, xFields, yFields)
    const selectFields = determineFieldsAvailableToDrag(query, xFields, yFields)

    const updateSortUI = (i: boolean) => {
        if (i) {
            setSort(0)
            setTopTen(false)
        }
        setSortDisabled(i)
    }

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {distance: 8}
        })
    )

    const dragEvent = (event: DragEndEvent) => {
        if (event.over) {
            const {field, from} = (event.active.data.current as DragCurrent)
            if (event.over.id === 'select' && from !== 'Select') {
                removeFieldIfPresent(field, xFields, setXFields)
                removeFieldIfPresent(field, yFields, setYFields)
                setIsDefaultChartModified(true)
            } else if (event.over.id === 'x-axis' && from !== 'X') {
                setXFields([...xFields, field])
                removeFieldIfPresent(field, yFields, setYFields)
                setIsDefaultChartModified(true)
            } else if (event.over.id === 'y-axis' && from !== 'Y') {
                setYFields([...yFields, field])
                removeFieldIfPresent(field, xFields, setXFields)
                setIsDefaultChartModified(true)
            }
        }
    }

    if (dragEventRef !== undefined) {
        dragEventRef.current = dragEvent
    }

    const chartOptions: ChartOptions = {
        queryId,
        chartType,
        xFields,
        yFields,
        values: valueField ? [valueField] : [],
        filters: relevantFilters,
        sort: sort,
        topTen: topTen
    }

    return <>

        <div className={styles.title_container}>
            <QueryBuilderLogo className={qbStyles.logo}/>
            <div className={styles.title}>
                Query Visualizer
            </div>
            <QueryBuilderTabs currentTab={QUERY_BUILDER_TAB.QUERY_VISUALISER}
                              setQueryBuilderTab={setQueryBuilderTab}
                              isQueryVisualizerTabEnabled={isQueryVisualizerTabEnabled}
                              isCohortAnalyser={isCohortAnalyser}
            />
        </div>

        <DndContext sensors={sensors} onDragEnd={dragEvent}>

            <Row wrap={false}>

                <Col flex="100px">
                    <ChartTypeSelector
                        selectedChart={chartType}
                        onSelectChart={(type) => {
                            setChartType(type)
                            setIsDefaultChartModified(true)
                        }}
                    />
                </Col>

                <Col flex={"auto"}>

                    <Row gutter={[8, 8]}>

                        <Col>
                            <ValueFields
                                queryMetadata={queryMetaData}
                                selectedValue={valueField}
                                chartType={chartType}
                                setSelectedValue={(val) => {
                                    setValueField(val)
                                    setIsDefaultChartModified(true)
                                }}
                            />
                        </Col>

                        <Col flex={24}>
                            <InitialFields isLoading={isMetadataLoading}>
                                <LineChartOutlined className={styles.fieldTypeIcon} />
                                <span className={styles.fieldTypeLabel}>Fields</span>
                                <SelectFields
                                    queryMetaData={queryMetaData}
                                    fields={selectFields}
                                    from="Select"
                                    appliedFilters={appliedFilters}
                                    setAppliedFilters={(filters) => {
                                        setAppliedFilters(filters)
                                        setIsDefaultChartModified(true)
                                    }}
                                />
                                <DragAndDropArea label={'Fields can be dragged from or returned to this area'}/>
                            </InitialFields>

                        </Col>

                        <Col span={24}>
                            <XAxis>
                                <SelectFields
                                    queryMetaData={queryMetaData}
                                    fields={xFields}
                                    from="X"
                                    appliedFilters={appliedFilters}
                                    setAppliedFilters={(filters) => {
                                        setAppliedFilters(filters)
                                        setIsDefaultChartModified(true)
                                    }}
                                />
                            </XAxis>
                        </Col>

                        <Col span={24}>
                            <YAxis chartType={chartType}>
                                <SelectFields
                                    queryMetaData={queryMetaData}
                                    fields={yFields}
                                    from="Y"
                                    appliedFilters={appliedFilters}
                                    setAppliedFilters={(filters) => {
                                        setAppliedFilters(filters)
                                        setIsDefaultChartModified(true)
                                    }}
                                />
                            </YAxis>
                        </Col>

                        <QueryVisualizerFilterDetails filterLabels={query?.filterLabels}/>

                        <Col span={24}>
                            <div className={styles.sortPanel}>
                                    <span className={styles.sortLabelContainer}>
                                        <SortIcon className={styles.sortIcon}/>
                                        Sort
                                    </span>
                                    <Radio.Group
                                        onChange={(e: RadioChangeEvent) => {
                                            setTopTen(e.target.value)
                                            setIsDefaultChartModified(true)
                                        }}
                                        value={topTen}
                                        disabled={isSortDisabled}
                                    >
                                        <Radio.Button value={false}>All</Radio.Button>
                                        <Radio.Button value={true}>View 10</Radio.Button>
                                    </Radio.Group>
                            <span>
                                    <Radio.Group
                                        onChange={(e: RadioChangeEvent) => {
                                            setSort(e.target.value)
                                            setIsDefaultChartModified(true)
                                        }}
                                        value={sort}
                                        disabled={isSortDisabled}
                                    >
                                        <Radio.Button value={-1}><SortDescendIcon/> Descending</Radio.Button>
                                        <Radio.Button value={0}><BarChartOutlined style={{fontSize: '1.2em'}}/> Default</Radio.Button>
                                        <Radio.Button value={1}><SortAscendIcon/> Ascending</Radio.Button>
                                    </Radio.Group>
                                </span>
                            </div>
                        </Col>


                        <Col span={24}>
                            {(queryId !== undefined || defaultQueryVisualisationOptions !== undefined) ? (
                                <Chart
                                    chartOptions={isDefaultChartModified ? chartOptions : (defaultQueryVisualisationOptions || chartOptions)}
                                    defaultChartOptions={!isDefaultChartModified ? defaultQueryVisualisationOptions : undefined}
                                    defaultResults={!isDefaultChartModified ? defaultQueryVisualisationResults : undefined}
                                    aggtype={valueField?.aggtype || defaultQueryVisualisationOptions?.values?.[0]?.aggtype}
                                    remainingFields={selectFields.length}
                                    setSortDisabled={updateSortUI}
                                />
                            ) : (
                                <Spin/>
                            )}
                        </Col>
                    </Row>

                </Col>
            </Row>

        </DndContext>

    </>

}